import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BadgeCheck,
  BookOpen,
  Briefcase,
  Code2,
  Compass,
  GraduationCap,
  HeartHandshake,
  Rocket,
  ShieldCheck,
  Sparkles,
  Star,
  Trophy,
  Users,
} from 'lucide-react';
import useInView from '../hooks/useInView';

const FadeIn = ({ children, delay = 0, className = '' }) => {
  const [ref, inView] = useInView(0.1);

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const SectionLabel = ({ children }) => (
  <p className="text-xs font-semibold tracking-[0.2em] uppercase text-purple-400 mb-3 font-outfit">
    {children}
  </p>
);

const DarkPanel = ({ children, className = '', innerClassName = '' }) => (
  <div
    className={`rounded-[2rem] border border-white/10 bg-white/[0.035] backdrop-blur-2xl transition-all duration-300 hover:border-cyan-300/25 hover:bg-white/[0.055] ${className}`}
    style={{ boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06), 0 0 36px rgba(0,240,255,0.045)' }}
  >
    <div className={innerClassName}>{children}</div>
  </div>
);

const pathway = [
  {
    icon: ShieldCheck,
    step: '01',
    title: 'Start in ROMIND KIDS',
    tag: 'Safe childhood layer',
    desc: 'Children begin in a protected creative world with clubs, challenges, identity, friend-based communication and RONI support.',
  },
  {
    icon: Sparkles,
    step: '02',
    title: 'Show Initiative',
    tag: 'Create, help, participate',
    desc: 'Long-term positive activity can reveal creativity, teamwork, responsibility, leadership and interest in technology.',
  },
  {
    icon: GraduationCap,
    step: '03',
    title: 'Grow through Academy',
    tag: 'Merit-based pathway',
    desc: 'Motivated children can move through Academy missions, badges, leadership roles, supervised challenges and project-based learning.',
  },
  {
    icon: Rocket,
    step: '04',
    title: 'Move into ROMIND TEENS',
    tag: 'Next age-level platform',
    desc: 'As children become teenagers, they move into a deeper platform for ages around 15–18 with stronger projects and mentorship.',
  },
  {
    icon: Briefcase,
    step: '05',
    title: 'Compete for Future Talent',
    tag: 'Strict selection',
    desc: 'The strongest long-term participants can qualify for ROMIND Scholarship funding, internships and a long-term ROMIND career pathway.',
  },
];

const academySignals = [
  'Initiative and curiosity',
  'Respectful communication',
  'Creative contribution',
  'Teamwork in clubs',
  'Interest in IT and product logic',
  'Responsibility and consistency',
  'Digital safety awareness',
  'Desire to build, not only scroll',
];

const teenTracks = [
  { icon: Code2, title: 'IT challenges', desc: 'Coding logic, product thinking, testing, bug hunting and safer digital systems.' },
  { icon: Users, title: 'Team projects', desc: 'Supervised collaboration, role distribution, communication and leadership practice.' },
  { icon: BookOpen, title: 'Portfolio building', desc: 'A visible record of projects, club work, badges and long-term learning progress.' },
  { icon: HeartHandshake, title: 'Mentorship', desc: 'Guided growth, digital safety missions and preparation for future education.' },
];

const audienceBlocks = [
  {
    title: 'Why children should join',
    intro: 'Because their ideas matter.',
    points: [
      'They can become builders, creators, club leaders and team players.',
      'They receive recognition for effort, kindness and real participation.',
      'They learn to create with purpose instead of disappearing into endless scrolling.',
    ],
  },
  {
    title: 'Why parents may love it',
    intro: 'Safety is only the beginning.',
    points: [
      'Digital time becomes a structured growth path with clubs, challenges and achievements.',
      'Parents can see a future-facing environment, not just another social feed.',
      'Strong long-term participants can compete for real ROMIND Scholarship funding for university education, not only badges.',
    ],
  },
  {
    title: 'Why it strengthens ROMIND KIDS',
    intro: 'A strong community is built, not bought.',
    points: [
      'The platform grows a culture of responsibility, creativity and respectful leadership.',
      'Future mentors, interns and specialists can emerge from the same safe ecosystem.',
      'ROMIND can support young digital talent without positioning children as workers.',
    ],
  },
];

const RomindAcademy = () => (
  <main data-testid="romind-academy-page" className="bg-[#05050A] min-h-screen overflow-hidden">
    <section className="relative pt-32 pb-20 overflow-hidden">
      <div className="absolute top-16 left-1/4 w-96 h-96 bg-purple-600/12 rounded-full blur-[140px] pointer-events-none animate-nebula" />
      <div className="absolute bottom-6 right-1/4 w-80 h-80 bg-cyan-500/10 rounded-full blur-[120px] pointer-events-none animate-nebula" />
      <div
        className="absolute inset-0 pointer-events-none opacity-35"
        style={{
          background:
            'radial-gradient(circle at 15% 20%, rgba(157,76,221,0.18), transparent 28%), radial-gradient(circle at 80% 18%, rgba(0,240,255,0.12), transparent 28%), radial-gradient(circle at 50% 85%, rgba(255,0,127,0.12), transparent 34%)',
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <FadeIn className="lg:col-span-7">
            <SectionLabel>ROMIND KIDS Academy</SectionLabel>
            <h1 className="font-outfit font-black text-5xl sm:text-6xl lg:text-7xl text-white tracking-tight leading-[1.03] mb-6">
              Safety is the start.<br />
              <span className="gradient-text">A stronger future is the goal.</span>
            </h1>
            <p className="text-white/62 text-base sm:text-lg leading-relaxed max-w-2xl mb-8">
              ROMIND KIDS Academy is not just a nice educational layer. It is a long-term leadership and technology pathway for children who show effort, talent, discipline and initiative in the IT field — a path where the strongest Academy graduates can compete for a real ROMIND Scholarship: tuition and education funding for IT-focused study at an approved leading university anywhere in the world, including Ivy League-level or other internationally recognized institutions where admission and scholarship eligibility are met, and the opportunity to build a future career inside the ROMIND ecosystem.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/parents"
                data-testid="academy-parents-cta"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm bg-purple-600/80 border border-purple-400/45 text-white hover:bg-purple-500 transition-all duration-200 shadow-lg shadow-purple-900/35"
              >
                Why parents may love it <ArrowRight size={14} />
              </Link>
              <Link
                to="/clubs-challenges"
                data-testid="academy-clubs-cta"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm bg-white/5 border border-white/15 text-white/80 hover:text-white hover:bg-white/10 hover:border-cyan-300/35 transition-all duration-200"
              >
                Explore clubs & challenges <ArrowRight size={14} />
              </Link>
              <Link to="/romind-lab" data-testid="academy-lab-cta" className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm bg-cyan-500/10 border border-cyan-300/35 text-cyan-100 hover:bg-cyan-500/20 transition-all duration-200">{'ROMIND LAB'} <ArrowRight size={14} /></Link>
            </div>
          </FadeIn>

          <FadeIn delay={180} className="lg:col-span-5">
            <DarkPanel innerClassName="p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-11 h-11 rounded-2xl bg-cyan-500/10 border border-cyan-300/25 flex items-center justify-center">
                  <Compass size={20} className="text-cyan-300" />
                </div>
                <div>
                  <p className="font-outfit font-bold text-white text-lg">Safe today.</p>
                  <p className="text-white/45 text-xs">Skilled tomorrow. Ready for the future.</p>
                </div>
              </div>
              <div className="space-y-4">
                {[
                  'ROMIND KIDS is the first step into a safer digital childhood.',
                  'ROMIND TEENS is the next age-level platform as children grow older.',
                  'ROMIND Academy is the long-term growth path across both stages.',
                  'The strongest Academy graduates can compete for funded ROMIND Scholarship education.',
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3 rounded-2xl bg-white/[0.03] border border-white/8 p-4">
                    <Star size={15} className="text-cyan-300 mt-0.5 flex-shrink-0" />
                    <p className="text-white/62 text-sm leading-relaxed">{item}</p>
                  </div>
                ))}
              </div>
            </DarkPanel>
          </FadeIn>
        </div>
      </div>
    </section>

    <section className="relative py-20 bg-[#080714] overflow-hidden">
      <div className="absolute top-0 right-0 w-80 h-80 bg-purple-600/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn>
          <SectionLabel>The Architecture</SectionLabel>
          <div className="max-w-3xl mb-10">
            <h2 className="font-outfit font-bold text-3xl sm:text-4xl text-white mb-4 tracking-tight">
              ROMIND KIDS is the beginning.<br />
              <span className="gradient-text-cool">ROMIND Academy is the serious growth pathway.</span>
            </h2>
            <p className="text-white/55 text-base leading-relaxed">
              ROMIND TEENS is not a prize for only the best. It is the next age-level social platform for teenagers. ROMIND Academy connects both stages into one long-term pathway across childhood, teenage growth and future preparation.
            </p>
          </div>
        </FadeIn>

        <FadeIn delay={120}>
          <DarkPanel className="mb-7" innerClassName="p-6 sm:p-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
              <div className="lg:col-span-4">
                <div className="w-12 h-12 rounded-2xl bg-purple-500/10 border border-purple-300/20 flex items-center justify-center mb-5">
                  <Rocket size={20} className="text-purple-300" />
                </div>
                <h3 className="font-outfit font-bold text-2xl text-white mb-3">A long-term head start</h3>
                <p className="text-white/45 text-sm leading-relaxed">
                  This is the part parents understand immediately: the child is not just spending time online. The child is building a future advantage.
                </p>
              </div>
              <div className="lg:col-span-8 space-y-4 text-white/58 text-sm sm:text-base leading-relaxed">
                <p>
                  ROMIND Academy is designed to give children a long-term head start. Starting from age 9, children can begin developing digital confidence, communication, teamwork, creativity, problem-solving and IT thinking inside a safe ecosystem.
                </p>
                <p>
                  As they grow into ROMIND TEENS, this journey can become deeper: more advanced projects, stronger mentorship, portfolio building, supervised challenges and future career preparation.
                </p>
                <p>
                  By the time a young person reaches their first official career step, they may already have years of guided digital growth, project experience, Academy achievements and a strong understanding of the ROMIND ecosystem. This means they are not starting from zero. They are growing with purpose.
                </p>
              </div>
            </div>
          </DarkPanel>
        </FadeIn>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {pathway.map((item, i) => (
            <FadeIn key={item.title} delay={i * 70}>
              <DarkPanel innerClassName="h-full p-5">
                <div className="flex items-center justify-between mb-5">
                  <div className="w-11 h-11 rounded-2xl bg-white/[0.04] border border-white/10 flex items-center justify-center">
                    <item.icon size={19} className="text-cyan-300" />
                  </div>
                  <span className="font-outfit font-black text-3xl text-white/10">{item.step}</span>
                </div>
                <p className="text-[10px] font-outfit uppercase tracking-[0.18em] text-purple-200/70 mb-2">{item.tag}</p>
                <h3 className="font-outfit font-bold text-white text-lg mb-3 leading-tight">{item.title}</h3>
                <p className="text-white/50 text-xs leading-relaxed">{item.desc}</p>
              </DarkPanel>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>

    <section className="relative py-20 overflow-hidden">
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-500/10 rounded-full blur-[130px] pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          <FadeIn className="lg:col-span-5">
            <SectionLabel>Who Academy Notices</SectionLabel>
            <h2 className="font-outfit font-bold text-3xl sm:text-4xl text-white mb-5 tracking-tight">
              Recognition for effort,<br />
              <span className="gradient-text">not empty activity.</span>
            </h2>
            <p className="text-white/55 text-base leading-relaxed mb-6">
              ROMIND KIDS Academy is not about exploiting children or turning them into workers. It is a voluntary educational pathway for young users who show initiative, responsibility, creativity and interest in building something meaningful.
            </p>
            <div className="rounded-3xl bg-white/[0.035] border border-white/8 p-5">
              <p className="text-white/42 text-xs leading-relaxed">
                The promise is serious, but the selection is serious too. ROMIND Scholarship is intended for the strongest long-term Academy participants who meet strict published criteria, maintain strong academic progress, prove discipline inside the ecosystem and earn their place through consistent effort.
              </p>
            </div>
          </FadeIn>

          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {academySignals.map((signal, i) => (
              <FadeIn key={signal} delay={i * 45}>
                <div className="rounded-2xl bg-white/[0.035] border border-white/8 p-4 flex items-center gap-3 hover:border-cyan-300/25 hover:bg-white/[0.055] transition-all duration-200">
                  <div className="w-8 h-8 rounded-xl bg-purple-500/10 border border-purple-400/20 flex items-center justify-center flex-shrink-0">
                    <BadgeCheck size={15} className="text-purple-300" />
                  </div>
                  <span className="text-white/68 text-sm font-medium">{signal}</span>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </div>
    </section>

    <section className="relative py-20 bg-[#080714] overflow-hidden">
      <div className="absolute top-20 left-1/2 w-80 h-80 bg-purple-600/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn>
          <SectionLabel>ROMIND TEENS</SectionLabel>
          <div className="max-w-3xl mb-10">
            <h2 className="font-outfit font-bold text-3xl sm:text-4xl text-white mb-4 tracking-tight">
              The next age-level platform, not a reward.
            </h2>
            <p className="text-white/55 text-base leading-relaxed">
              Children grow into ROMIND TEENS because they become teenagers. The Academy continues through this stage for the most motivated participants, with stronger IT-focused learning, supervised projects, digital safety missions, mentorship and future university preparation.
            </p>
          </div>
        </FadeIn>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {teenTracks.map((track, i) => (
            <FadeIn key={track.title} delay={i * 70}>
              <DarkPanel innerClassName="h-full p-6">
                <div className="w-11 h-11 rounded-2xl bg-cyan-500/10 border border-cyan-300/20 flex items-center justify-center mb-5">
                  <track.icon size={19} className="text-cyan-300" />
                </div>
                <h3 className="font-outfit font-bold text-white text-lg mb-3">{track.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed">{track.desc}</p>
              </DarkPanel>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>

    <section className="relative py-20 overflow-hidden">
      <div className="absolute top-0 right-1/4 w-72 h-72 bg-cyan-500/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn>
          <SectionLabel>Why It Matters</SectionLabel>
          <h2 className="font-outfit font-bold text-3xl sm:text-4xl text-white mb-10 tracking-tight max-w-3xl">
            ROMIND KIDS is not just protecting children online.<br />
            <span className="gradient-text-cool">It is helping them grow into the future.</span>
          </h2>
        </FadeIn>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {audienceBlocks.map((block, i) => (
            <FadeIn key={block.title} delay={i * 90}>
              <DarkPanel innerClassName="h-full p-6 sm:p-7">
                <div className="w-10 h-10 rounded-2xl bg-white/[0.04] border border-white/10 flex items-center justify-center mb-5">
                  {i === 0 && <Sparkles size={18} className="text-purple-300" />}
                  {i === 1 && <HeartHandshake size={18} className="text-cyan-300" />}
                  {i === 2 && <Trophy size={18} className="text-purple-300" />}
                </div>
                <h3 className="font-outfit font-bold text-xl mb-2 text-white">
                  {block.title}
                </h3>
                <p className="text-white/70 text-sm font-semibold mb-5">{block.intro}</p>
                <div className="space-y-3">
                  {block.points.map((point) => (
                    <div key={point} className="flex items-start gap-2.5">
                      <div className="w-4 h-4 rounded-full bg-cyan-500/15 border border-cyan-300/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <div className="w-1.5 h-1.5 rounded-full bg-cyan-300" />
                      </div>
                      <p className="text-white/50 text-sm leading-relaxed">{point}</p>
                    </div>
                  ))}
                </div>
              </DarkPanel>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>

    <section className="relative py-20 bg-[#080714] overflow-hidden">
      <div className="absolute inset-0 pointer-events-none opacity-30" style={{ background: 'radial-gradient(circle at 30% 20%, rgba(157,76,221,0.18), transparent 30%), radial-gradient(circle at 80% 80%, rgba(0,240,255,0.14), transparent 34%)' }} />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn>
          <SectionLabel>Scholarship Pathway</SectionLabel>
          <div className="max-w-4xl mb-10">
            <h2 className="font-outfit font-black text-4xl sm:text-5xl text-white tracking-tight leading-tight mb-6">
              The end point must be clear:<br />
              <span className="gradient-text">real education support for the best.</span>
            </h2>
            <p className="text-white/58 text-base sm:text-lg leading-relaxed">
              ROMIND Academy is designed so that the strongest long-term participants can compete for the ROMIND Scholarship — a serious financial commitment for selected Academy graduates, not a decorative badge. For those who prove discipline, talent, teamwork, academic performance and long-term progress, ROMIND Scholarship means financial funding for IT-focused university education at an approved leading university anywhere in the world, followed by internships and a long-term career pathway inside the ROMIND ecosystem.
            </p>
          </div>
        </FadeIn>

        <FadeIn delay={60}>
          <DarkPanel className="mb-8" innerClassName="p-6 sm:p-8 lg:p-9">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-7 items-start">
              <div className="lg:col-span-4">
                <div className="w-12 h-12 rounded-2xl bg-purple-500/10 border border-purple-300/20 flex items-center justify-center mb-5">
                  <Star size={21} className="text-purple-300" />
                </div>
                <h3 className="font-outfit font-bold text-2xl text-white mb-3">Why a $30 doorway can matter</h3>
                <p className="text-white/45 text-sm leading-relaxed">
                  For parents, this is the practical hook: a small monthly doorway can help a child stay inside a serious growth system long enough to be noticed.
                </p>
              </div>
              <div className="lg:col-span-8 space-y-4 text-white/58 text-sm sm:text-base leading-relaxed">
                <p>
                  ROMIND KIDS Academy is built around a simple parent truth: the child is not paying for another endless feed. The child is entering a structured ecosystem where clubs, challenges, badges, creative work, IT tasks, teamwork and Academy progress can become part of a long-term portfolio.
                </p>
                <p>
                  Month after month and year after year, a child who shows effort, discipline, curiosity, kindness, strong school progress and real participation can build a visible record. Parents are not just watching screen time disappear. They are helping their child stay on a pathway where consistent work can be seen, guided and seriously evaluated.
                </p>
                <p>
                  The ROMIND Scholarship is the serious end point of that pathway for selected top Academy graduates: financial funding for IT-focused study at an approved leading university anywhere in the world, followed by a structured return into internships, professional training and long-term work inside the ROMIND ecosystem. It is not a lottery and not automatic. It is a chance earned through years of visible effort.
                </p>
              </div>
            </div>
          </DarkPanel>
        </FadeIn>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 mb-8">
          <FadeIn delay={80} className="lg:col-span-7">
            <DarkPanel innerClassName="p-6 sm:p-8 h-full">
              <div className="w-12 h-12 rounded-2xl bg-cyan-500/10 border border-cyan-300/20 flex items-center justify-center mb-5">
                <GraduationCap size={21} className="text-cyan-300" />
              </div>
              <h3 className="font-outfit font-bold text-2xl text-white mb-4">For Families With More Than One Child</h3>
              <div className="space-y-4 text-white/58 text-sm sm:text-base leading-relaxed">
                <p>
                  Many parents are not worried about only one future. They are thinking about the future of every child in the family.
                </p>
                <p>
                  ROMIND KIDS Academy gives children a chance to grow through their own effort, talent, discipline and initiative. For families with more than one child, this matters even more: every child deserves the possibility to be noticed, supported and guided toward future opportunities.
                </p>
                <p>
                  The Academy cannot promise the same outcome for every child. But it can create a fairer path where motivation, creativity, teamwork and long-term progress can open doors that money alone should not decide.
                </p>
              </div>
            </DarkPanel>
          </FadeIn>

          <FadeIn delay={140} className="lg:col-span-5">
            <DarkPanel innerClassName="p-6 sm:p-8 h-full">
              <div className="w-12 h-12 rounded-2xl bg-purple-500/10 border border-purple-300/20 flex items-center justify-center mb-5">
                <Trophy size={21} className="text-purple-300" />
              </div>
              <h3 className="font-outfit font-bold text-2xl text-white mb-4">What ROMIND Scholarship means</h3>
              <div className="space-y-4 text-white/58 text-sm sm:text-base leading-relaxed">
                <p>
                  ROMIND Scholarship is not a symbolic medal. It is a serious Academy promise for the strongest selected graduates: ROMIND will fund IT-focused university education at an approved leading university anywhere in the world, according to the official scholarship package.
                </p>
                <p>
                  The goal is clear: a talented child should not lose their potential because the family cannot afford the next level. If the child earns the place, ROMIND helps open the door.
                </p>
                <p className="text-white/42 text-xs leading-relaxed">
                  Selection is strict. Scholarship rules, covered costs, university approval, parental consent, legal requirements and the post-graduation work agreement will be defined in the official ROMIND Scholarship Agreement.
                </p>
              </div>
            </DarkPanel>
          </FadeIn>
        </div>

        <FadeIn delay={180}>
          <DarkPanel className="mb-8" innerClassName="p-6 sm:p-8 lg:p-9">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-7 items-start">
              <div className="lg:col-span-4">
                <div className="w-12 h-12 rounded-2xl bg-white/[0.04] border border-white/10 flex items-center justify-center mb-5">
                  <Compass size={21} className="text-cyan-300" />
                </div>
                <h3 className="font-outfit font-bold text-2xl text-white mb-3">More Than One Path Forward</h3>
                <p className="text-white/45 text-sm leading-relaxed">
                  Scholarship is the highest path, not the only value of growing inside ROMIND Academy.
                </p>
              </div>
              <div className="lg:col-span-8 space-y-4 text-white/58 text-sm sm:text-base leading-relaxed">
                <p>
                  The ROMIND Scholarship Fund is designed for the best of the best. Not every Academy participant will receive a scholarship, and selection will be highly competitive.
                </p>
                <p>
                  But ROMIND Academy is valuable beyond the scholarship pathway. Children who grow through the Academy can build skills, confidence, project experience, communication habits, teamwork, digital responsibility and a long-term portfolio.
                </p>
                <p>
                  Even if a participant is not selected for scholarship support, their Academy journey may still help them access future opportunities inside the ROMIND ecosystem after they reach the appropriate working age — such as community support, club coordination, safety assistance, moderation support, project assistance or other paid roles.
                </p>
                <p>
                  These opportunities may allow young adults to continue studying at university while contributing remotely from anywhere in the world, earning income, gaining real experience and staying connected to the ecosystem they grew up in.
                </p>
                <p className="text-white/72 font-semibold">
                  ROMIND Academy is not only about one final prize. It is about building a future path where effort, discipline and growth can matter.
                </p>
              </div>
            </div>
          </DarkPanel>
        </FadeIn>

        <FadeIn delay={240}>
          <DarkPanel className="mb-8" innerClassName="p-6 sm:p-8 lg:p-9">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-7 items-start">
              <div className="lg:col-span-4">
                <div className="w-12 h-12 rounded-2xl bg-cyan-500/10 border border-cyan-300/20 flex items-center justify-center mb-5">
                  <Briefcase size={21} className="text-cyan-300" />
                </div>
                <h3 className="font-outfit font-bold text-2xl text-white mb-3">The Scholarship Agreement</h3>
                <p className="text-white/45 text-sm leading-relaxed">
                  The promise must be clear for parents: ROMIND invests in the best, and the best return to build with ROMIND.
                </p>
              </div>
              <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  {
                    title: 'Funded university path',
                    text: 'Selected Academy graduates receive financial funding for IT-focused study at an approved leading university anywhere in the world.',
                  },
                  {
                    title: 'Return to ROMIND',
                    text: 'After graduation, recipients enter internships, training and a professional role inside the ROMIND ecosystem.',
                  },
                  {
                    title: '10-year career commitment',
                    text: 'A standard pathway includes a 10-year ROMIND career commitment, helping graduates grow into stable, experienced professionals.',
                  },
                ].map((item) => (
                  <div key={item.title} className="rounded-2xl bg-white/[0.035] border border-white/8 p-5">
                    <h4 className="font-outfit font-bold text-white text-base mb-3">{item.title}</h4>
                    <p className="text-white/52 text-sm leading-relaxed">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </DarkPanel>
        </FadeIn>

        <FadeIn delay={260}>
          <div className="text-center">
            <div className="flex flex-wrap justify-center gap-3">
              <Link
                to="/testing"
                data-testid="academy-testing-cta"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm bg-purple-600/80 border border-purple-400/45 text-white hover:bg-purple-500 transition-all duration-200"
              >
                Join early testing <ArrowRight size={14} />
              </Link>
              <Link
                to="/contact"
                data-testid="academy-contact-cta"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm bg-white/5 border border-white/15 text-white/80 hover:text-white hover:bg-white/10 transition-all duration-200"
              >
                Contact ROMIND KIDS <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>
  </main>
);

export default RomindAcademy;
